/**
 * perfectDiffuser.ts - "Perfect Diffuser" aeration-device specification
 *                       calibrated for Barcombe-class shallow eutrophic
 *                       UK reservoirs, with array placement + seasonal
 *                       performance evaluation + adjustment feedback.
 *
 * Defensibility:
 *   - Design defaults match the synthesised design template (Section 5 of
 *     the Aeration Knowledge Base)
 *   - Bubble physics and OTE drawn from McGinnis 2006 + ASCE 2-06
 *   - Cost benchmark from Stenstrom 2008 + Ofwat PR19
 *   - Per-season inputs are documented Barcombe seasonal envelopes
 */

import type { ScenarioInputs } from './scenarioManager';
import { assessReservoirHealth, type ReservoirHealthAssessment } from './reservoirHealth';
import { BARCOMBE_BATHY } from './barcombeBathymetry';

// ------------------------------------------------------------------------
// 1. Device specification
// ------------------------------------------------------------------------

export type DiffuserMembrane = 'EPDM' | 'silicone' | 'ceramic-dome';
export type ArrayLayout = 'hex' | 'square' | 'line' | 'deep-cluster';
export type DutyMode = 'continuous' | 'day-only' | 'seasonal' | 'pulsed-30min';

export interface PerfectDiffuserSpec {
  // --- Device (per unit) -----------------------------
  /** Bubble release diameter (mm). */
  bubble_d_mm: number;
  /** Membrane pore size (-m). */
  poreSize_um: number;
  /** Membrane material - drives lifetime + fouling drift. */
  membrane: DiffuserMembrane;
  /** Disc diameter (mm). */
  discDiameter_mm: number;
  /** Air flow rate per device (L/s at NTP). */
  Qair_perDevice_Ls: number;
  /** Operating depth (m below water surface). */
  releaseDepth_m: number;
  /** Height above sediment bed (m). */
  heightAboveBed_m: number;

  // --- Array (multiple units) -------------------------
  /** Number of devices in array. */
  count: number;
  /** Geometric layout pattern. */
  layout: ArrayLayout;
  /** Spacing factor - plume diameter. */
  spacingFactor: number;
  /** Bias toward deepest 30 % of basin (1.0 = strong bias, 0.0 = uniform). */
  deepZoneBias: number;

  // --- Operating regime -------------------------------
  /** Duty cycle pattern. */
  dutyMode: DutyMode;
  /** Duty cycle % when not continuous (0-100). */
  dutyPercent: number;

  // --- Commercial -------------------------------------
  /** Capital cost per device (-). */
  capexPerDevice: number;
  /** Power per device (kW) - computed from Q_air. */
  powerPerDevice_kW?: number;
}

/** Default Perfect Diffuser specification - Barcombe-tuned. */
export const PERFECT_DIFFUSER_DEFAULT: PerfectDiffuserSpec = {
  bubble_d_mm: 2.0,
  poreSize_um: 1500,
  membrane: 'EPDM',
  discDiameter_mm: 300,
  Qair_perDevice_Ls: 2.0,
  releaseDepth_m: 4.0,
  heightAboveBed_m: 0.5,
  count: 16,
  layout: 'hex',
  spacingFactor: 1.8,
  deepZoneBias: 0.7,
  dutyMode: 'continuous',
  dutyPercent: 100,
  capexPerDevice: 2200,
};

// ------------------------------------------------------------------------
// 2. Performance prediction
// ------------------------------------------------------------------------

export interface DevicePerformance {
  /** Plume diameter at release depth (m). */
  plumeDiameter_m: number;
  /** Effective OTE (%) per McGinnis 2006 simplified. */
  ote_pct: number;
  /** O- delivered per device per day (kg). */
  o2PerDevice_kgDay: number;
  /** Array total O- delivery (kg/d). */
  o2Total_kgDay: number;
  /** Power per device (kW). */
  power_kW: number;
  /** Whole-array power (kW). */
  powerTotal_kW: number;
  /** Cost per kg O- delivered (-). */
  costPerKgO2: number;
  /** Plume FOOTPRINT (area where plumes break the surface) as fraction of basin. Target 1.5 - 15 %. */
  coverageFraction: number;
  /** Surface MIXING coverage (Cooke 2005 empirical influence radius) as fraction of basin. Target > 80 %. */
  surfaceMixingCoverage: number;
  /** -/yr OPEX (electricity only). */
  opex_GBPperYear: number;
  /** Total CAPEX (-). */
  capex_GBP: number;
  /** Compressed-air pressure-drop (bar). */
  manifoldPressureBar: number;
}

export function computeDevicePerformance(spec: PerfectDiffuserSpec, basinArea_m2: number): DevicePerformance {
  // Plume diameter - Asaeda-Imberger / Wuest detrainment scaling
  const plumeDiameter_m = 0.34 * spec.releaseDepth_m * 6;

  // McGinnis 2006 / ASCE 2-06: kLa per metre = 0.08 * (3 / d_bubble_mm); OTE
  // = 1 - exp(-z * kLa). Matches strategyCYearRound (single source of truth).
  const kLa_perM = 0.08 * (3 / Math.max(spec.bubble_d_mm, 0.5));
  const ote = Math.min(0.92, 1 - Math.exp(-spec.releaseDepth_m * kLa_perM));

  // O2 mass: Q_air[L/s] x 0.001 (m3/L) x 1.225 (kg/m3 air) x 0.2315 (O2 mass
  // fraction) x OTE x 86400 (s/day) => kg O2 / day per device.
  const dutyEff = spec.dutyMode === 'continuous' ? 1.0
                : spec.dutyMode === 'day-only'   ? 0.50
                : spec.dutyMode === 'pulsed-30min' ? 0.50
                : spec.dutyPercent / 100;
  const o2PerDevice_kgDay = spec.Qair_perDevice_Ls * 0.001 * 1.225 * 0.2315 * ote * dutyEff * 86400;
  const o2Total_kgDay = o2PerDevice_kgDay * spec.count;

  // Power: P - Q - -P / - - -P grows with depth
  const dP_bar = 0.10 + spec.releaseDepth_m * 0.10;
  const eta = 0.65;
  const power_kW = (spec.Qair_perDevice_Ls * 0.001 * dP_bar * 1e5) / (eta * 1000);
  const powerTotal_kW = power_kW * spec.count * dutyEff;

  // -/kg O2 - electricity at -0.22/kWh + amortised CAPEX
  const elec_GBPperKgO2 = (power_kW * 24 / Math.max(o2PerDevice_kgDay, 0.001)) * 0.22;
  const capex_GBPperKgO2 = spec.capexPerDevice / (10 * 365 * o2PerDevice_kgDay);    // 10-yr life
  const costPerKgO2 = elec_GBPperKgO2 + capex_GBPperKgO2;

  // PLUME FOOTPRINT: total plume area / basin area, capped at 100% (target 1.5 - 15 %)
  const totalPlumeArea = spec.count * Math.PI * Math.pow(plumeDiameter_m / 2, 2);
  const coverageFraction = Math.min(1, totalPlumeArea / basinArea_m2);

  // SURFACE MIXING COVERAGE: Cooke 2005 empirical influence radius (target > 80 %)
  // baseInfluence 2500 m^2 per device, scaled by sqrt(depth/4) and sqrt(Q_air/2), x 0.65 packing efficiency
  const baseInfluenceArea = 2500;
  const depthFactor = Math.pow(spec.releaseDepth_m / 4.0, 0.5);
  const qFactor     = Math.pow(spec.Qair_perDevice_Ls / 2.0, 0.5);
  const influenceArea_perDevice = baseInfluenceArea * depthFactor * qFactor;
  const surfaceMixingCoverage = Math.min(1, spec.count * influenceArea_perDevice * 0.65 / basinArea_m2);

  const capex_GBP = spec.capexPerDevice * spec.count;
  const opex_GBPperYear = powerTotal_kW * 24 * 365 * 0.22;

  return {
    plumeDiameter_m, ote_pct: ote * 100,
    o2PerDevice_kgDay, o2Total_kgDay,
    power_kW, powerTotal_kW,
    costPerKgO2, coverageFraction, surfaceMixingCoverage,
    opex_GBPperYear, capex_GBP,
    manifoldPressureBar: dP_bar,
  };
}

// ------------------------------------------------------------------------
// 3. Seasonal scenarios for Barcombe
// ------------------------------------------------------------------------

export type Season = 'winter' | 'spring' | 'summer' | 'autumn';

export const SEASON_LABEL: Record<Season, string> = {
  winter: 'Winter (Dec-Feb)',
  spring: 'Spring (Mar-May)',
  summer: 'Summer (Jun-Aug)',
  autumn: 'Autumn (Sep-Nov)',
};

const BASE_BARCOMBE: Omit<ScenarioInputs, 'doSurface_mgL' | 'doHypo_mgL' | 'orp_mV' | 'feII_mgL' | 'mnII_mgL'> = {
  // Geometry from Shoreline Surveys bathymetry (single source of truth).
  area_m2: BARCOMBE_BATHY.area_m2,
  depth_mean_m: BARCOMBE_BATHY.meanDepth_m,
  depth_max_m: BARCOMBE_BATHY.maxDepth_m,
  chl_ugL: 30, nom_mgL: 8, tn_mgL: 1.2, tp_mgL: 0.08, pH: 7.6,
  surfaceTempC: 19, hypoTempC: 11, stratified: true, fCyano: 0.45,
  windMs: 2.5, N_brunt: 0.012,
  diffuserQair_Ls: 2.0,
  diffuserDepth_m: Math.max(2.5, BARCOMBE_BATHY.maxDepth_m - 0.5),
  bubble_d_mm: 2.5,
};

export function seasonalBarcombe(season: Season, spec: PerfectDiffuserSpec): ScenarioInputs {
  // Estimate the DO uplift from the spec
  const perf = computeDevicePerformance(spec, BASE_BARCOMBE.area_m2);
  const o2Boost = Math.min(7, perf.o2Total_kgDay / 5);  // simplified - boost in mg/L hypolimnion
  const orpBoost = Math.min(280, perf.coverageFraction * 250);

  const seasons: Record<Season, ScenarioInputs> = {
    winter: {
      ...BASE_BARCOMBE,
      surfaceTempC: 5, hypoTempC: 4.5, stratified: false,
      chl_ugL: 4, fCyano: 0.10,
      doSurface_mgL: 11.5, doHypo_mgL: 11,
      orp_mV: 280, feII_mgL: 0.04, mnII_mgL: 0.03,
      windMs: 4.5, N_brunt: 0.004,
      diffuserQair_Ls: spec.Qair_perDevice_Ls,
      diffuserDepth_m: spec.releaseDepth_m,
      bubble_d_mm: spec.bubble_d_mm,
    },
    spring: {
      ...BASE_BARCOMBE,
      surfaceTempC: 13, hypoTempC: 9, stratified: false,
      chl_ugL: 14, fCyano: 0.25,
      doSurface_mgL: 10, doHypo_mgL: Math.min(11, 7.5 + o2Boost * 0.5),
      orp_mV: Math.min(280, 170 + orpBoost * 0.3),
      feII_mgL: 0.10, mnII_mgL: 0.08,
      windMs: 3.2, N_brunt: 0.008,
      diffuserQair_Ls: spec.Qair_perDevice_Ls,
      diffuserDepth_m: spec.releaseDepth_m,
      bubble_d_mm: spec.bubble_d_mm,
    },
    summer: {
      ...BASE_BARCOMBE,
      surfaceTempC: 22, hypoTempC: 13, stratified: true,
      chl_ugL: 30, fCyano: 0.45,
      doSurface_mgL: 8.5,
      doHypo_mgL: Math.min(8, 1.5 + o2Boost),
      orp_mV: Math.min(280, 50 + orpBoost),
      feII_mgL: Math.max(0.05, 0.60 - perf.coverageFraction * 0.5),
      mnII_mgL: Math.max(0.03, 0.45 - perf.coverageFraction * 0.4),
      windMs: 2.0, N_brunt: 0.018,
      diffuserQair_Ls: spec.Qair_perDevice_Ls,
      diffuserDepth_m: spec.releaseDepth_m,
      bubble_d_mm: spec.bubble_d_mm,
    },
    autumn: {
      ...BASE_BARCOMBE,
      surfaceTempC: 14, hypoTempC: 12, stratified: false,
      chl_ugL: 18, fCyano: 0.35,
      doSurface_mgL: 9.5, doHypo_mgL: Math.min(10, 6 + o2Boost * 0.7),
      orp_mV: Math.min(280, 130 + orpBoost * 0.5),
      feII_mgL: 0.30, mnII_mgL: 0.20,
      windMs: 3.8, N_brunt: 0.010,
      diffuserQair_Ls: spec.Qair_perDevice_Ls,
      diffuserDepth_m: spec.releaseDepth_m,
      bubble_d_mm: spec.bubble_d_mm,
    },
  };
  return seasons[season];
}

// ------------------------------------------------------------------------
// 4. Seasonal evaluation
// ------------------------------------------------------------------------

export interface SeasonResult {
  season: Season;
  inputs: ScenarioInputs;
  health: ReservoirHealthAssessment;
}

export interface AnnualEvaluation {
  perDevice: DevicePerformance;
  seasons: SeasonResult[];
  /** Annual aggregate health score (mean across 4 seasons). */
  annualScore: number;
  /** Worst season label. */
  worstSeason: Season;
  /** Annual status. */
  annualStatus: 'ok' | 'warn' | 'critical';
}

export function evaluateAnnual(spec: PerfectDiffuserSpec): AnnualEvaluation {
  const perDevice = computeDevicePerformance(spec, BASE_BARCOMBE.area_m2);
  const seasonsList: Season[] = ['winter', 'spring', 'summer', 'autumn'];
  const seasons: SeasonResult[] = seasonsList.map(s => {
    const inputs = seasonalBarcombe(s, spec);
    const health = assessReservoirHealth(inputs);
    return { season: s, inputs, health };
  });
  const annualScore = seasons.reduce((a, r) => a + r.health.overallScore, 0) / seasons.length;
  const worst = seasons.reduce((min, r) => r.health.overallScore < min.health.overallScore ? r : min, seasons[0]);
  const annualStatus: 'ok' | 'warn' | 'critical' = annualScore >= 75 ? 'ok' : annualScore >= 50 ? 'warn' : 'critical';
  return { perDevice, seasons, annualScore, worstSeason: worst.season, annualStatus };
}

// ------------------------------------------------------------------------
// 5. Adjustment feedback - "what to tune for biggest improvement"
// ------------------------------------------------------------------------

export interface AdjustmentSuggestion {
  parameter: keyof PerfectDiffuserSpec;
  direction: 'increase' | 'decrease' | 'change';
  currentValue: string;
  suggestedValue: string;
  expectedScoreGain: number;
  rationale: string;
  affects: string[];
}

/** Perturbation candidates for the auto-feedback engine. */
function perturbCandidates(spec: PerfectDiffuserSpec): Array<{
  param: keyof PerfectDiffuserSpec;
  newSpec: PerfectDiffuserSpec;
  desc: string;
  affects: string[];
}> {
  return [
    { param: 'count',
      newSpec: { ...spec, count: Math.min(40, spec.count + 4) },
      desc: `add 4 devices - ${spec.count + 4}`,
      affects: ['Plume coverage', 'O- supply margin', 'Sediment ORP'] },
    { param: 'count',
      newSpec: { ...spec, count: Math.max(4, spec.count - 2) },
      desc: `reduce by 2 - ${Math.max(4, spec.count - 2)}`,
      affects: ['CAPEX / OPEX', 'Cost per kg O-'] },
    { param: 'Qair_perDevice_Ls',
      newSpec: { ...spec, Qair_perDevice_Ls: Math.min(6, spec.Qair_perDevice_Ls + 0.5) },
      desc: `+0.5 L/s air - ${(spec.Qair_perDevice_Ls + 0.5).toFixed(1)} L/s`,
      affects: ['DO at sediment interface', 'Stratification breakdown time'] },
    { param: 'bubble_d_mm',
      newSpec: { ...spec, bubble_d_mm: Math.max(0.8, spec.bubble_d_mm - 0.5) },
      desc: `finer bubbles - ${Math.max(0.8, spec.bubble_d_mm - 0.5).toFixed(1)} mm`,
      affects: ['OTE', '- per kg O-', 'Bed shear at modules'] },
    { param: 'releaseDepth_m',
      newSpec: { ...spec, releaseDepth_m: Math.min(7.5, spec.releaseDepth_m + 0.5) },
      desc: `release 0.5 m deeper - ${(spec.releaseDepth_m + 0.5).toFixed(1)} m`,
      affects: ['OTE (depth-resolved)', 'Plume reach'] },
    { param: 'spacingFactor',
      newSpec: { ...spec, spacingFactor: Math.min(2.5, spec.spacingFactor + 0.2) },
      desc: `spacing - ${(spec.spacingFactor + 0.2).toFixed(1)} - plume -`,
      affects: ['Plume coverage', 'Asaeda-Imberger merging regime'] },
    { param: 'deepZoneBias',
      newSpec: { ...spec, deepZoneBias: Math.min(1.0, spec.deepZoneBias + 0.1) },
      desc: `deep-zone bias - ${Math.min(1.0, spec.deepZoneBias + 0.1).toFixed(1)}`,
      affects: ['Plume vertical reach', 'Sediment ORP at deep cells'] },
    { param: 'dutyMode',
      newSpec: { ...spec, dutyMode: 'continuous' as DutyMode, dutyPercent: 100 },
      desc: `switch to continuous duty`,
      affects: ['DO sustained', 'Stratification breakdown'] },
    { param: 'membrane',
      newSpec: { ...spec, membrane: 'ceramic-dome' as DiffuserMembrane, bubble_d_mm: Math.max(1.5, spec.bubble_d_mm) },
      desc: `switch to ceramic dome (longer life, slightly coarser)`,
      affects: ['Maintenance cycle', 'Fouling drift'] },
  ];
}

export function generateAdjustments(currentSpec: PerfectDiffuserSpec): AdjustmentSuggestion[] {
  const baseline = evaluateAnnual(currentSpec);
  const candidates = perturbCandidates(currentSpec);
  const suggestions: AdjustmentSuggestion[] = [];

  candidates.forEach(c => {
    const trial = evaluateAnnual(c.newSpec);
    const gain = trial.annualScore - baseline.annualScore;
    if (gain >= 1.0) {
      const oldV = String(currentSpec[c.param]);
      const newV = String(c.newSpec[c.param]);
      suggestions.push({
        parameter: c.param,
        direction: typeof currentSpec[c.param] === 'number' && Number(newV) > Number(oldV)
                    ? 'increase' : typeof currentSpec[c.param] === 'number' ? 'decrease' : 'change',
        currentValue: oldV,
        suggestedValue: newV,
        expectedScoreGain: gain,
        rationale: c.desc,
        affects: c.affects,
      });
    }
  });

  return suggestions.sort((a, b) => b.expectedScoreGain - a.expectedScoreGain).slice(0, 6);
}

// ------------------------------------------------------------------------
// 6. Placement on bathymetry - deepest 30 % bias hex grid
// ------------------------------------------------------------------------

import type { SiteBathymetry } from './benchmarkSites';
import { buildDepthRaster } from './bathymetryField';

export interface PlacedDevice {
  id: string;
  x: number; y: number;      // relative 0-1
  x_m: number; y_m: number;
  depth_m: number;
  inDeepZone: boolean;
}

export function autoPlaceDevices(
  spec: PerfectDiffuserSpec,
  bath: SiteBathymetry,
  depthMax: number
): PlacedDevice[] {
  const raster = buildDepthRaster(bath, depthMax);
  const N = raster.N;
  const cells: Array<{ i: number; j: number; depth: number; inDeep: boolean }> = [];
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const d = raster.depths[j][i];
      if (isFinite(d)) cells.push({ i, j, depth: d, inDeep: d >= raster.deepThreshold });
    }
  }
  // Sort by deep-bias score: weighted by depth + small randomness for grid stagger
  const targetCount = spec.count;
  // Deterministic hex-grid generation with deep-zone bias
  // Algorithm: filter to deep-zone if bias > 0.5, else uniform; then sample evenly
  const candidatePool = spec.deepZoneBias > 0.5
    ? cells.filter(c => c.inDeep)
    : cells;
  // Build a hex grid: choose N points spread across candidate pool by skipping
  const ranked = candidatePool.sort((a, b) =>
    // Deep first if bias high
    (b.depth - a.depth) * spec.deepZoneBias + (Math.random() * 0.001));
  // Greedy spaced selection - enforce minimum spacing
  const minSpacingCells = Math.max(2, Math.round(N / Math.sqrt(targetCount) * spec.spacingFactor / 2));
  const placed: PlacedDevice[] = [];
  for (const c of ranked) {
    if (placed.length >= targetCount) break;
    const tooClose = placed.some(p => {
      const dx = (c.i / N) - p.x;
      const dy = (c.j / N) - p.y;
      return Math.hypot(dx * N, dy * N) < minSpacingCells;
    });
    if (tooClose) continue;
    const x = (c.i + 0.5) / N;
    const y = (c.j + 0.5) / N;
    placed.push({
      id: `pd_${placed.length}`,
      x, y,
      x_m: x * bath.extent_m.width,
      y_m: y * bath.extent_m.height,
      depth_m: c.depth,
      inDeepZone: c.inDeep,
    });
  }
  // If we couldn't place enough due to spacing, relax and add remaining
  if (placed.length < targetCount) {
    for (const c of ranked) {
      if (placed.length >= targetCount) break;
      if (placed.find(p => Math.abs(p.x - (c.i + 0.5) / N) < 1e-6 && Math.abs(p.y - (c.j + 0.5) / N) < 1e-6)) continue;
      const x = (c.i + 0.5) / N;
      const y = (c.j + 0.5) / N;
      placed.push({
        id: `pd_${placed.length}`,
        x, y,
        x_m: x * bath.extent_m.width,
        y_m: y * bath.extent_m.height,
        depth_m: c.depth,
        inDeepZone: c.inDeep,
      });
    }
  }
  return placed;
}
